import Intl, { LOCALE_KEY } from '@fx-ui/intl';
import { imageSourceIntl, LANG, LOCAL_STORAGE_MIGRATE, QueryKey } from '~/constants';
import {
    formatUrlSuffix,
    get,
    getApiUrl,
    includes,
    isEmpty,
    join,
    JSONParse,
    map,
    split,
    values
} from '@libs/shared';

/**
 * 获取国际化图片资源文件（用于不同语言加载不同图片资源场景，目前仅区分中英文，不作繁体区分）
 *
 * @param localeKey
 */
export const getLocaleImg = (localeKey: string): string => {
    return imageSourceIntl.get(localeKey);
};

/**
 * 是否启用用户画像收集步骤
 */
export const hasFunctionRegisterPersona = () => {
    return get(window.jdy_config, `function_map.register_persona`, true);
};

/**
 * 产品平台手机号功能是否开启
 */
export const hasFunctionPlatformPhone = () => {
    return get(window.jdy_config, `function_map.platform_phone`, true);
};

/**
 * 产品通讯录手机号功能是否开启
 */
export const hasFunctionContactPhone = () => {
    return get(window.jdy_config, `function_map.contact_phone`, true);
};

/**
 * 是否具备导入免审功能
 */
export const hasFunctionExemptTrialImport = () => {
    return get(window.jdy_config, `function_map.direct_user_provisioning`, false);
};

/**
 * 是否支持在个人设置页设置微信绑定
 */
export const hasFunctionUserProfileWechat = () => {
    return get(window.jdy_config, `function_map.user_profile_wechat`, true);
};

/**
 * 当前环境是否有帆软通行证渠道
 */
export const hasFunctionFrPassport = () => {
    return window.jdy_config.function_map.fr_passport ?? false;
};

/**
 * 当前环境是否有模板中心语言筛选导航项
 */
export const hasFunctionTemplateLang = () => {
    return window.jdy_config.function_map.tpl_center_lang ?? false;
};

/**
 * 产品内google登录相关功能是否启用
 */
export const hasFunctionGoogle = () => {
    return get(window.jdy_config, `function_map.oauth_google`, false);
};

/**
 * 产品内多因素登录验证是否启用
 */
export const hasFunctionMfa = () => {
    return get(window.jdy_config, `function_map.mfa`, false);
};

/**
 * 产品内启信宝服务是否启用
 */
export const hasFunctionQixinSearch = () => {
    return get(window.jdy_config, `function_map.qixin_search_service`, false);
};

/**
 * 是否显示注销成员按钮
 */
export const hasFunctionDeleteAccount = () => {
    return get(window.jdy_config, 'function_map.user_cancel', false);
};

export const hasFunctionProductCenter = () =>
    get(window.jdy_config, ['function_map', 'product_center'], false);

/**
 * 是否显示订单信息页面
 */
export const hasFunctionOrderInfo = (): boolean => {
    return get(window.jdy_config, 'function_map.order_info', false);
};

/** 插件是否支持付费方案 */
export const hasFunctionPluginPayment = () => {
    return get(window.jdy_config, ['function_map', 'open_plugin_payment'], false);
};

/**
 * 产品互联组织功能是否开启
 */
export const hasFunctionCorpCoop = (): boolean => {
    return get(window.jdy_config, `function_map.corp_coop`, true);
};

/**
 * 身份认证产品功能是否启用
 */
export const hasFunctionTenantVerify = () => {
    return get(window.jdy_config, `function_map.tenant_verify`, true);
};

/**
 * 是否展示重新认证入口
 */
export const hasFunctionReTenantVerify = () => {
    return get(window.jdy_config, `function_map.re_tenant_verify`, true);
};

/**
 * 是否展示技术支持
 * 私有云环境下进行屏蔽
 */
export const hasFunctionSupport = () => {
    return get(window.jdy_config, `function_map.support`, false);
};

/**
 * 产品内第三方平台绑定引导是否启用
 */
export const hasFunctionUserThirdPartyGuide = () => {
    return get(window.jdy_config, `function_map.user_third_party_guide`, true);
};

/**
 * 是否展示【企业管理】-【公司信息完善】入口
 * 独享版环境不展示
 */
export const hasFunctionUserPersona = () => {
    return get(window.jdy_config, `function_map.user_persona`, true);
};

/**
 * 产品内登录日志的地址信息服务是否启用
 */
export const hasFunctionLoginLocation = () => {
    return get(window.jdy_config, `function_map.login_location_service`, false);
};

/**
 * 是否显示互联组织批量导入客体企业
 */
export const hasFunctionGuestCorpImport = () => {
    return get(window.jdy_config, 'function_map.guest_corp_import', true);
};

/**
 *产品微信服务号绑定功能是否开启
 */
export const hasFunctionCorpWechat = () => {
    return get(window.jdy_config, `function_map.corp_wechat`, true);
};

/**
 * 产品内AI实验室功能是否启用
 */
export const hasFunctionAILab = () => {
    return get(window.jdy_config, `function_map.ai_lab`, true);
};

/**
 * 是否展示【企业管理】-【高级设置】-【登录二次验证】开关
 * 独享版展示，公有云不展示
 */
export const hasFunctionCorpMfa = () => {
    return get(window.jdy_config, `function_map.corp_mfa`, false);
};

/**
 * 获取当前环境匿名用户可访问的语言类型列表
 */
export const getAnonymousLocaleKeyList = () => {
    return get(window.jdy_config, 'anonymous_locale_list', [
        LOCALE_KEY.ZH_CN,
        LOCALE_KEY.ZH_TW,
        LOCALE_KEY.EN_US
    ]) as LOCALE_KEY[];
};

/**
 * 获取当前环境默认语言列表
 */
export const getDefaultLocaleList = () => {
    return map(getDefaultLocaleKeyList(), (key) => ({
        label: LANG[key],
        value: key
    }));
};

/**
 * 获取当前环境默认语言类型列表
 */
const getDefaultLocaleKeyList = () => {
    return get(window.jdy_config, 'locale_list', values(LOCALE_KEY)) as LOCALE_KEY[];
};

/**
 * 获取默认语言类型
 */
export const getDefaultLocaleKey = () => {
    return get(window.jdy_config, 'locale', get(getDefaultLocaleKeyList(), '[0]'));
};

/**
 * 获取服务器系统地区
 */
export const getServerRegion = () => {
    return get(window.jdy_config, 'region');
};

/**
 * 获取当前语言类型
 */
export const getCurrentLocaleKey = (): LOCALE_KEY => {
    const defaultLocaleKey = getDefaultLocaleKey();
    // locale 获取： localStorage -> 浏览器
    const localeKey = JSONParse(
        Intl.determineLocale({
            localStorageLocaleKey: LOCAL_STORAGE_MIGRATE.LANG
        }).toLowerCase()
    );
    let currentLocaleKey = Intl.getLocaleKey(localeKey) || defaultLocaleKey;

    const currentLocaleKeyList = getDefaultLocaleKeyList();
    /**
     * 如果当前语言不在（后台）配置的语言列表当中，则默认采用（后台）默认配置语言（即语言列表第一个）
     * eg. jodoo环境后台只配置了英文语言，但是当前语言读出来是中文（即浏览器默认语言环境），此时界面语言应该是英文语言而不是浏览器设置的中文语言
     */
    if (!includes(currentLocaleKeyList, currentLocaleKey)) {
        currentLocaleKey = defaultLocaleKey;
    }
    return currentLocaleKey;
};

/**
 * 是否允许创建企业
 */
export const hasFunctionTenantRegister = () =>
    get(window.jdy_config, 'function_map.tenant_register', true);

/**
 * 是否开启云币功能 目前海外版关闭，国内版打开
 */
export const hasFunctionCoin = () => {
    return get(window.jdy_config, 'function_map.coin', true);
};

/**
 * 是否开启手机认证功能
 */
export const hasFunctionPhoneAuthn = () => {
    return get(window.jdy_config, 'function_map.phone_authn', true);
};

/** 成员api */
export const hasFunctionMemberApi = () => {
    return get(window.jdy_config, ['function_map', 'member_api'], true);
};

export enum STATIC_SITE_ROUTER_TYPE {
    SITE = 'site', // 站点
    KMS = 'kms' // 知识库
}

/**
 * 获取站点路径
 *
 * @param pattern
 * @param args
 * @deprecated 推荐使用 `siteRouterManager.getRelativePath` 替代
 */
export const getSiteRouter = (pattern: string, ...args: (string | number)[]) => {
    return getApiUrl(pattern, ...args);
};

/**
 * 获取页面路由
 */
export const getRouter = (pattern: string, ...args: (string | number)[]) => {
    return getApiUrl(pattern, ...args);
};

/**
 * 获取带参站点路径
 *
 * @param query
 * @param pattern
 * @param args
 * @deprecated 推荐使用 `siteRouterManager.getQueryRelativePath` 替代
 */
export const getQueryRouter = (
    query: { key: QueryKey; value: string | number }[],
    pattern: string,
    ...args: (string | number)[]
) => {
    if (isEmpty(query)) {
        return getSiteRouter(pattern, ...args);
    }
    const queryStr = join(
        map(query, (item) => {
            return `${item.key}=${item.value}`;
        }),
        '&'
    );
    const routerArr = split(getApiUrl(pattern, ...args), '#');
    const baseRouter = routerArr.shift();
    return `${baseRouter}?${queryStr}${isEmpty(routerArr) ? '' : '#' + routerArr.join('')}`;
};

/**
 * 获取固定站点路径：内链、外链、知识库
 *
 * @param type
 * @param pattern
 * @param args
 * @deprecated 推荐使用 `siteRouterManager.getAbsolutePath` 替代
 */
export const getStaticSiteRouter = (
    type: STATIC_SITE_ROUTER_TYPE,
    pattern: string,
    ...args: (string | number)[]
) => {
    switch (type) {
        case STATIC_SITE_ROUTER_TYPE.SITE:
            return formatUrlSuffix(getHostSite(), getApiUrl(pattern, ...args));
        case STATIC_SITE_ROUTER_TYPE.KMS:
            return formatUrlSuffix(getKMSSite(), getApiUrl(pattern, ...args));
        default:
            return getSiteRouter(pattern, ...args);
    }
};
/**
 * @param pattern
 * @param {...any} args
 */
export const getApi = (pattern: string, ...args: (string | number)[]) => {
    return getApiUrl(pattern, ...args);
};

/**
 * 获取站点域名
 */
const getHostSite = () => {
    return get(window, ['jdy_config', 'site'], '');
};

/**
 * 获取KMS站点
 */
const getKMSSite = () => {
    return get(window, 'jdy_config.kms_site', '');
};

export const getCsrfToken = () => {
    return {
        'X-CSRF-Token': window.jdy_csrf_token
    };
};

/**
 * 文件下载
 * tips: 不要使用 window.open/a 标签下载文件，在钉钉集成模式下体验不好
 * @param downloadUrl 下载链接
 */
export const downloadFile = (downloadUrl: string) => {
    location.href = downloadUrl;
};

export const getOdmId = () => {
    return window.jdy_config?.odm_id;
};

export const isMobileUA = () => !!window.jdy_config?.isMobile;
